import type { ReactiveController, ReactiveControllerHost } from 'lit'
import type { OverlayLayer } from '../OverlayLayerStack.js'
import { DismissOverlaysEvent } from '../DismissOverlaysEvent.js'
import { overlayLayers } from '../OverlayLayerStack.js'
import { EventController } from './EventController.js'
import { ShortcutController } from './ShortcutController.js'

export interface LightDismissOptions {
  isOpen: () => boolean
  onDismiss: EventListener
  isDismissible?: (node: EventTarget) => boolean
  /**
   * When `true`, the host registers with the shared overlay layer stack while
   * open, so it is dismissed when another overlay opens (a
   * `DismissOverlaysEvent` / `nord-dismiss-overlays` is dispatched on
   * `document`). Off by default so hosts opt in explicitly. `ModalController`
   * must NOT set this: an already-open modal would otherwise close itself when a
   * second modal opens, breaking nested modals. The reason is passed to
   * `onDismiss` as a `DismissOverlaysEvent` (`event.type === "nord-dismiss-overlays"`)
   * so hosts can, for example, skip restoring focus to a trigger that is about
   * to sit behind the opening overlay.
   */
  dismissOnOverlayOpen?: boolean
}

export class LightDismissController implements ReactiveController {
  private shortcut: ShortcutController
  private events: EventController

  private isMouseDownOutside = false
  private layer?: OverlayLayer
  private unregisterLayer?: () => void

  constructor(
    private host: ReactiveControllerHost & Element,
    private options: LightDismissOptions,
  ) {
    host.addController(this)
    this.shortcut = new ShortcutController(host, { Escape: this.handleEsc })
    this.events = new EventController(host)
  }

  hostConnected() {
    this.events.listen(document, 'mousedown', this.handleMouseDown, true)
    this.events.listen(document, 'click', this.handleClick, true)
  }

  hostUpdated() {
    if (!this.options.dismissOnOverlayOpen) {
      return
    }

    // Track the host's open state into the shared overlay layer stack: register
    // while open, unregister on close. When another overlay opens, the stack
    // calls `dismiss`, which routes back through `onDismiss` with a
    // `DismissOverlaysEvent` so the host can tell it apart from pointer/Escape.
    const open = this.options.isOpen()
    if (open && !this.unregisterLayer) {
      this.layer = {
        element: this.host,
        dismiss: () => this.options.onDismiss(new DismissOverlaysEvent()),
      }
      this.unregisterLayer = overlayLayers.register(this.layer)
    }
    else if (!open && this.unregisterLayer) {
      this.unregisterLayer()
      this.unregisterLayer = undefined
      this.layer = undefined
    }
  }

  hostDisconnected() {
    this.unregisterLayer?.()
    this.unregisterLayer = undefined
    this.layer = undefined
  }

  private handleEsc = (e: KeyboardEvent) => {
    if (!this.options.isOpen()) {
      return
    }

    // When this host participates in the overlay layer stack, only the topmost
    // layer dismisses on Escape, so one press closes just the top overlay (e.g.
    // a submenu inside a dropdown) rather than the whole stack. Hosts that do
    // not register (`this.layer` undefined) keep their existing Escape.
    if (this.layer && !overlayLayers.isTopmost(this.layer)) {
      return
    }

    this.options.onDismiss(e)
  }

  private handleMouseDown = (e: MouseEvent) => {
    if (!this.options.isOpen()) {
      return
    }

    const predicate: LightDismissOptions['isDismissible'] = this.options.isDismissible ?? (node => node !== this.host)
    this.isMouseDownOutside = e.composedPath().every(predicate)
  }

  private handleClick = (e: MouseEvent) => {
    if (!this.options.isOpen()) {
      return
    }

    const predicate: LightDismissOptions['isDismissible'] = this.options.isDismissible ?? (node => node !== this.host)
    const isClickOutside = e.composedPath().every(predicate)

    const shouldDismiss = isClickOutside && this.isMouseDownOutside

    if (shouldDismiss) {
      this.options.onDismiss(e)
    }
  }
}
